import React from "react";
import { Box } from "@mui/material";

import { ProjectDetailPageProps } from "../../../types";
import { useCostsData } from "./hooks/useCostsData";
import { useProjectAnlagen } from "../../map";
import PageTemplate from "../../layout/PageTemplate";
import CapexSection from "./sections/CapexSection";
import OpexSection from "./sections/OpexSection";
import YearlyCostsSection from "./sections/YearlyCostsSection";

interface CostsPageProps extends ProjectDetailPageProps {
  canView: boolean;
  canEdit: boolean;
}

/**
 * Costs Page Component
 * Detailansicht der Kosten eines Windpark-Projekts
 * Item-basiertes Modell mit flexiblen Kostenpositionen
 *
 * Performance-optimiert: YearlyCosts werden client-seitig via useMemo berechnet.
 * Hook-Funktionen werden direkt als Props übergeben (kein Wrapper).
 */
const CostsPage: React.FC<CostsPageProps> = ({ project, canEdit }) => {
  const {
    capex,
    opex,
    yearlyCosts,
    isLoading,
    error,
    isSaving,
    createCapexItem,
    updateCapexItem,
    deleteCapexItem,
    createOpexItem,
    updateOpexItem,
    deleteOpexItem,
    updateYearlyValue,
    createLeaseTier,
    updateLeaseTier,
    deleteLeaseTier,
    setGenehmigungOverride,
    updateCapexSectionLabel,
    updateOpexSectionLabel,
  } = useCostsData({
    projectId: project.id,
    laufzeitJahre: project.laufzeit_jahre ?? 20,
  });

  const { anlagen } = useProjectAnlagen(project.id);
  const anzahlAnlagen = anlagen.length;

  return (
    <PageTemplate
      isLoading={isLoading}
      error={!capex && !opex ? error : undefined}
      isSaving={isSaving}
      saveError={error}
    >
      {/* Investitionskosten Section */}
      <Box sx={{ mb: 3 }}>
        <CapexSection
          capex={capex}
          canEdit={canEdit}
          anzahlAnlagen={anzahlAnlagen}
          onItemCreate={createCapexItem}
          onItemUpdate={updateCapexItem}
          onItemDelete={deleteCapexItem}
          onSectionLabelChange={canEdit ? updateCapexSectionLabel : undefined}
          onGenehmigungOverrideChange={
            canEdit ? setGenehmigungOverride : undefined
          }
        />
      </Box>

      {/* Betriebskosten Section */}
      <Box sx={{ mb: 3 }}>
        <OpexSection
          opex={opex}
          canEdit={canEdit}
          onItemCreate={createOpexItem}
          onItemUpdate={updateOpexItem}
          onItemDelete={deleteOpexItem}
          jahr1Kosten={yearlyCosts?.jahre?.[0]?.kosten ?? {}}
          projektLaufzeit={project.laufzeit_jahre!}
          onLeaseTierChange={updateLeaseTier}
          onLeaseTierAdd={createLeaseTier}
          onLeaseTierDelete={deleteLeaseTier}
          onSectionLabelChange={canEdit ? updateOpexSectionLabel : undefined}
        />
      </Box>

      {/* Jährliche Kostenübersicht */}
      <Box sx={{ mt: 4 }}>
        <YearlyCostsSection
          data={yearlyCosts}
          canEdit={canEdit}
          onYearlyValueChange={updateYearlyValue}
          opexItems={opex?.items}
        />
      </Box>
    </PageTemplate>
  );
};

export default CostsPage;
